/**
 * Session Health Monitor
 *
 * Detects orphaned sessions by tracking the anomaly pattern:
 * "refresh succeeds but request still fails with 401"
 *
 * When this happens 3+ times in a 30-second window, it indicates a genuine
 * backend issue (permissions revoked, token revoked, API misconfiguration)
 * rather than a transient network error.
 *
 * Logs distinct Sentry events so we can distinguish:
 * - Transient 401s (1-2 failures, usually resolved)
 * - Genuine anomalies (3+ failures, need to sign out)
 * - Refresh failures (different root cause)
 */

interface FailureRecord {
  timestamp: number;
  endpoint: string;
  method: string;
}

let failureHistory: FailureRecord[] = [];

// Configuration: adjust based on production data
const FAILURE_THRESHOLD = 3; // How many 401s before anomaly?
const TIME_WINDOW_MS = 30 * 1000; // How long to track (30s)?

/**
 * Record a failure: refresh succeeded but the retried request still got 401
 * Returns the current failure count in the time window
 */
export function recordRefreshSuccess401(
  endpoint: string,
  method: string,
): number {
  const now = Date.now();

  failureHistory.push({
    timestamp: now,
    endpoint,
    method,
  });

  // Clean up old entries outside the time window
  failureHistory = failureHistory.filter(
    (record) => now - record.timestamp < TIME_WINDOW_MS,
  );

  return failureHistory.length;
}

/**
 * Clear all failure records (call on successful request or before sign-out)
 */
export function clearFailureHistory(): void {
  failureHistory = [];
}

/**
 * Get the count of failures within the current time window
 */
export function getFailureCount(): number {
  const now = Date.now();
  return failureHistory.filter(
    (record) => now - record.timestamp < TIME_WINDOW_MS,
  ).length;
}

/**
 * Check if the failure pattern indicates a genuine session anomaly
 */
export function isSessionHealthAnomalous(): boolean {
  return getFailureCount() >= FAILURE_THRESHOLD;
}

/**
 * Get the current failure pattern for logging to Sentry
 * Returns records with age_ms instead of timestamp for clearer observability
 */
export function getFailurePattern(): Array<{
  endpoint: string;
  method: string;
  age_ms: number;
}> {
  const now = Date.now();
  return failureHistory
    .filter((record) => now - record.timestamp < TIME_WINDOW_MS)
    .map((record) => ({
      endpoint: record.endpoint,
      method: record.method,
      age_ms: now - record.timestamp,
    }));
}
